class TKProductMain {
  constructor(root) {
    this.root = root;
    this.product = this.safeJSON(root.dataset.productJson) || window.product || {};
    this.moneyFormat = root.dataset.moneyFormat || '${{amount}}';
    this.engravingVariantId = Number(root.dataset.engravingVariantId || 0);
    this.engravingPrice = Number(root.dataset.engravingPrice || 0);
    this.klaviyoPublicKey = root.dataset.klaviyoPublicKey || window.klaviyoPublicKey || window.KLAVIYO_PUBLIC_API_KEY || "";
    this.soldOutVariantIds = new Set(String(root.dataset.soldOutVariantIds || "").split(",").filter(Boolean).map(id => Number(id)));
    this.isNewSeriesProduct = root.dataset.newSeries === 'true';
    this.pendingAddToCartAfterAkoa = false;

    this.form = root.querySelector('[data-product-form]');
    this.variantInput = root.querySelector('[data-variant-id]');
    this.selects = [...root.querySelectorAll('[data-option-position]')];
    this.priceEls = [...root.querySelectorAll('[data-product-price-value]')];
    this.addToCart = root.querySelector('[data-add-to-cart]');
    this.addToCartText = root.querySelector('[data-add-to-cart-text]');
    this.actions = root.querySelector('[data-actions]');
    this.qty = root.querySelector('[data-quantity]');
    this.ensureInventoryBadgeMarkup();
    this.badge = root.querySelector('[data-inventory-badge]');
    this.ensureWaitlistMarkup();
    this.waitlist = root.querySelector('[data-waitlist]');
    this.sezzleWidget = root.querySelector('.sezzle-shopify-info-button');
    this.waitlistForm = root.querySelector('[data-waitlist-form]') || root.querySelector('#notify-me-container');
    this.waitlistSuccess = root.querySelector('[data-waitlist-success]');
    this.slides = [...root.querySelectorAll('.tk-product-media__slide')];
    this.lines = [...root.querySelectorAll('.tk-product-media__line')];

    this.personalization = root.querySelector('[data-personalization]');
    this.personalizationInput = root.querySelector('[data-personalization-input]');
    this.personalizationType = root.querySelector('[data-personalization-type]');

    this.init();
  }

  safeJSON(value) {
    try { return JSON.parse(value); } catch (e) { return null; }
  }

  init() {
    this.root.dataset.mediaWidth = this.root.querySelector('[data-product-main]')?.dataset?.mediaWidth || this.root.dataset.mediaWidth || '50';
    this.bindVariants();
    this.bindMedia();
    this.bindQuantity();
    this.bindPersonalization();
    this.bindGiftCard();
    this.bindWaitlist();
    this.bindAkoa();
    this.bindSubmit();
    this.updateFromVariant(this.currentVariant());
    this.updateDeliveryDate();
  }

  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return '$' + amount;
  }

  selectedOptions() {
    return this.selects.map(select => select.value);
  }

  currentVariant() {
    if (!this.product.variants) return null;

    const selected = this.selectedOptions();
    if (selected.length) {
      const match = this.product.variants.find(variant =>
        variant.options.every((option, index) => String(option) === String(selected[index]))
      );
      if (match) return match;
    }

    const id = this.variantInput ? Number(this.variantInput.value) : Number(this.root.dataset.currentVariantId);
    return this.product.variants.find(variant => Number(variant.id) === id) || this.product.variants[0] || null;
  }

  bindVariants() {
    this.selects.forEach(select => {
      select.addEventListener('change', () => {
        const variant = this.currentVariant();
        this.updateFromVariant(variant);
        this.updateMediaForVariant(variant);
      });
    });
  }

  bindMedia() {
    this.lines.forEach(line => {
      line.addEventListener('click', () => {
        const mediaIndex = Number(line.dataset.mediaIndex);
        this.setActiveMediaByIndex(mediaIndex);
      });
    });

    const previousButton = this.root.querySelector('[data-tk-media-prev]');
    const nextButton = this.root.querySelector('[data-tk-media-next]');

    if (previousButton) {
      previousButton.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.goToRelativeMedia(-1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.goToRelativeMedia(1);
      });
    }

    this.updateVisibleMedia(this.currentVariant());
  }

  goToRelativeMedia(direction) {
    const visibleSlides = this.slides.filter(slide => !slide.hidden);
    if (visibleSlides.length < 2) return;

    let currentIndex = visibleSlides.findIndex(slide => slide.classList.contains('is-active'));
    if (currentIndex < 0) currentIndex = 0;

    const nextIndex = (currentIndex + direction + visibleSlides.length) % visibleSlides.length;
    const nextSlide = visibleSlides[nextIndex];

    if (!nextSlide) return;

    this.setActiveMediaByIndex(Number(nextSlide.dataset.mediaIndex));
  }

  setActiveMediaByIndex(mediaIndex) {
    this.slides.forEach(slide => slide.classList.toggle('is-active', Number(slide.dataset.mediaIndex) === Number(mediaIndex)));
    this.lines.forEach(line => line.classList.toggle('is-active', Number(line.dataset.mediaIndex) === Number(mediaIndex)));
  }

  setActiveMedia(index) {
    const visibleSlides = this.slides.filter(slide => !slide.hidden);
    const target = visibleSlides[index] || visibleSlides[0] || this.slides[0];
    if (!target) return;
    this.setActiveMediaByIndex(Number(target.dataset.mediaIndex));
  }

  normalize(value) {
    return String(value || '').toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, ' ').trim();
  }

  updateVisibleMedia(variant) {
    if (!this.slides.length) return;

    const optionText = variant && variant.options ? variant.options.map(v => this.normalize(v)).join(' ') : '';
    let visibleIndexes = [];

    // Exact match first: photo keys look like "Black G10--Serrated" (color--blade) or "Forged Ember // Black Accents".
    // Every part of the key must equal one of the selected options, so "non-serrated" photos no longer show for "Serrated".
    if (variant && variant.options) {
      const selected = variant.options.map(v => this.normalize(v));
      this.slides.forEach(slide => {
        const parts = String(slide.dataset.altKey || '').split('--').map(p => this.normalize(p)).filter(Boolean);
        if (parts.length && parts.every(part => selected.includes(part))) visibleIndexes.push(Number(slide.dataset.mediaIndex));
      });
    }

    if (!visibleIndexes.length && optionText) {
      this.slides.forEach(slide => {
        const altKey = this.normalize(slide.dataset.altKey);
        const isMatch = altKey && optionText.split(' ').some(part => part.length > 2 && altKey.includes(part));
        if (isMatch) visibleIndexes.push(Number(slide.dataset.mediaIndex));
      });
    }

    if (!visibleIndexes.length && variant && variant.featured_media) {
      const slide = this.slides.find(item => Number(item.dataset.mediaId) === Number(variant.featured_media.id));
      if (slide) {
        const start = Math.max(0, this.slides.indexOf(slide));
        visibleIndexes = this.slides.slice(start, start + 5).map(item => Number(item.dataset.mediaIndex));
      }
    }

    if (!visibleIndexes.length) {
      visibleIndexes = this.slides.slice(0, 5).map(item => Number(item.dataset.mediaIndex));
    }

    const visibleSet = new Set(visibleIndexes.slice(0, 6));
    this.slides.forEach(slide => { slide.hidden = !visibleSet.has(Number(slide.dataset.mediaIndex)); });
    this.lines.forEach(line => { line.hidden = !visibleSet.has(Number(line.dataset.mediaIndex)); });
    this.setActiveMedia(0);
  }

  updateMediaForVariant(variant) {
    this.updateVisibleMedia(variant);
  }

  bindQuantity() {
    const minus = this.root.querySelector('[data-qty-minus]');
    const plus = this.root.querySelector('[data-qty-plus]');
    if (minus) minus.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); this.setQty(this.getQty() - 1); });
    if (plus) plus.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); this.setQty(this.getQty() + 1); });
  }

  getQty() {
    return this.qty ? Math.max(1, Number(this.qty.value) || 1) : 1;
  }

  setQty(value) {
    if (!this.qty) return;
    this.qty.value = Math.max(1, Number(value) || 1);
  }

  bindPersonalization() {
    if (!this.personalization) return;
    const open = this.root.querySelector('[data-personalization-open]');
    const remove = this.root.querySelector('[data-personalization-remove]');
    if (open) open.addEventListener('click', () => this.setPersonalization(true));
    if (remove) remove.addEventListener('click', () => this.setPersonalization(false));
  }

  setPersonalization(active) {
    if (!this.personalization || !this.personalizationInput || !this.personalizationType) return;
    this.personalization.classList.toggle('is-active', active);
    this.personalizationInput.disabled = !active;
    this.personalizationInput.required = active;
    this.personalizationType.value = active ? 'Personalized' : 'Standard';
    if (!active) this.personalizationInput.value = '';
    this.updateFromVariant(this.currentVariant());
    if (active) setTimeout(() => this.personalizationInput.focus(), 50);
  }

  isPersonalized() {
    return this.personalizationType && this.personalizationType.value === 'Personalized';
  }

  ensureInventoryBadgeMarkup() {
    if (this.root.querySelector('[data-inventory-badge]')) return;

    const titleWrap = this.root.querySelector('.tk-product-main__title-wrap') || this.root.querySelector('.tk-product-main__title');
    if (!titleWrap) return;

    const badge = document.createElement('span');
    badge.className = 'tk-product-badge outofstock--product';
    badge.setAttribute('data-inventory-badge', '');
    badge.hidden = true;

    titleWrap.insertAdjacentElement('beforebegin', badge);
  }

  bindGiftCard() {
    const offset = this.root.querySelector('.tekto-gift-recipient__offset');
    if (offset) offset.value = new Date().getTimezoneOffset();
    const message = this.root.querySelector('[data-gift-message]');
    const count = this.root.querySelector('[data-gift-message-count]');
    if (message && count) {
      message.addEventListener('input', () => { count.textContent = message.value.length; });
    }
  }

  ensureWaitlistMarkup() {
    if (this.root.querySelector('[data-waitlist]')) return;

    const insertAfter = this.actions || this.root.querySelector('.tk-product-block--buy') || this.form;
    if (!insertAfter) return;

    const waitlist = document.createElement('div');
    waitlist.className = 'tk-product-waitlist';
    waitlist.setAttribute('data-waitlist', '');
    waitlist.hidden = true;
    waitlist.innerHTML = `
      <button type="button" class="tk-product-waitlist__closed notify-klaviyo notify-form" data-waitlist-open>JOIN THE WAITLIST!</button>
      <div class="tk-product-waitlist__form klaviyoemail--box notify-form" id="notify-me-container" data-waitlist-form hidden>
        <input id="email-input" type="email" placeholder="Enter your e-mail address" autocomplete="email" data-waitlist-email>
        <button id="notify-me-button" type="button" data-waitlist-submit>JOIN THE WAITLIST!</button>
        <p data-waitlist-error hidden>Please input valid E-mail address</p>
      </div>
      <div class="tk-product-waitlist__thanks" data-waitlist-success hidden>We'll notify you when it's back</div>
    `;

    insertAfter.insertAdjacentElement('afterend', waitlist);
  }

  setWaitlistVisible(visible) {
    this.waitlist = this.root.querySelector('[data-waitlist]');
    this.waitlistForm = this.root.querySelector('[data-waitlist-form]') || this.root.querySelector('#notify-me-container');
    this.waitlistSuccess = this.root.querySelector('[data-waitlist-success]');

    if (!this.waitlist) return;

    this.waitlist.hidden = !visible;
    this.waitlist.style.display = visible ? 'block' : 'none';

    if (visible) {
      this.resetWaitlistState();
    }
  }

  bindWaitlist() {
    const open = this.root.querySelector('[data-waitlist-open]');
    const submit = this.root.querySelector('[data-waitlist-submit]');
    const email = this.root.querySelector('[data-waitlist-email]');
    const error = this.root.querySelector('[data-waitlist-error]');

    if (open && this.waitlistForm) {
      open.addEventListener('click', () => {
        this.root.querySelectorAll('[data-waitlist-open]').forEach(button => {
          button.hidden = true;
          button.setAttribute('aria-hidden', 'true');
          button.style.setProperty('display', 'none', 'important');
          button.style.setProperty('visibility', 'hidden', 'important');
          button.style.setProperty('opacity', '0', 'important');
        });

        this.root.querySelectorAll('[data-waitlist-success]').forEach(success => {
          success.hidden = true;
          success.style.setProperty('display', 'none', 'important');
        });

        this.root.querySelectorAll('[data-waitlist-form]').forEach(form => {
          form.hidden = false;
          form.style.setProperty('display', 'grid', 'important');
        });

        if (email) email.focus();
      });
    }

    if (submit && email) {
      submit.addEventListener('click', async () => {
        const emailValue = email.value.trim();
        const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);

        if (!valid) {
          if (error) {
            error.textContent = 'Please input valid E-mail address';
            error.hidden = false;
          }
          return;
        }

        const variant = this.currentVariant();
        if (!variant || !variant.id) {
          if (error) {
            error.textContent = 'Please select a product option first.';
            error.hidden = false;
          }
          return;
        }

        if (!this.klaviyoPublicKey) {
          if (error) {
            error.textContent = 'Klaviyo public API key is missing.';
            error.hidden = false;
          }
          return;
        }

        if (error) error.hidden = true;
        submit.disabled = true;
        submit.classList.add('is-loading');

        try {
          await this.submitKlaviyoBackInStock(emailValue, variant);
          this.showWaitlistSuccess();
        } catch (err) {
          if (error) {
            error.textContent = 'Something went wrong. Please try again.';
            error.hidden = false;
          }
          submit.disabled = false;
          submit.classList.remove('is-loading');
        }
      });
    }
  }

  submitKlaviyoBackInStock(email, variant) {
    const variantId = `$shopify:::$default:::${variant.id}`;

    return fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${encodeURIComponent(this.klaviyoPublicKey)}`, {
      method: 'POST',
      headers: {
        'revision': '2026-04-15',
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json'
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            channels: ['EMAIL'],
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: variantId
              }
            }
          }
        }
      })
    }).then(response => {
      if (!response.ok && response.status !== 202) {
        throw new Error('Klaviyo Back in Stock request failed');
      }
      return response;
    });
  }

  showWaitlistSuccess() {
    const open = this.root.querySelector('[data-waitlist-open]');
    this.root.querySelectorAll('[data-waitlist-open]').forEach(button => {
      button.hidden = true;
      button.setAttribute('aria-hidden', 'true');
      button.style.setProperty('display', 'none', 'important');
      button.style.setProperty('visibility', 'hidden', 'important');
      button.style.setProperty('opacity', '0', 'important');
    });
    if (this.waitlistForm) {
      this.waitlistForm.hidden = true;
      this.waitlistForm.style.display = 'none';
    }
    if (this.waitlistSuccess) {
      this.waitlistSuccess.hidden = false;
      this.waitlistSuccess.style.display = 'flex';
    }
  }

  bindAkoa() {
    const modal = this.root.querySelector('[data-akao-modal]');
    const trigger = this.root.querySelector('[data-akao-trigger]');
    const close = this.root.querySelector('[data-akao-close]');
    const accept = this.root.querySelector('[data-akao-accept]');
    if (!modal) return;

    this.akoaAccepted = false;
    if (trigger) trigger.addEventListener('click', () => { modal.hidden = false; });
    if (close) close.addEventListener('click', () => {
      modal.hidden = true;
      this.pendingAddToCartAfterAkoa = false;
    });
    if (accept) accept.addEventListener('click', () => {
      this.akoaAccepted = true;
      modal.hidden = true;

      if (this.pendingAddToCartAfterAkoa) {
        this.pendingAddToCartAfterAkoa = false;
        this.handleProductSubmit();
      }
    });
  }

  bindSubmit() {
    if (!this.form) return;

    if (this.addToCart) {
      this.addToCart.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        this.handleProductSubmit();
      });
    }

    this.form.addEventListener('submit', event => {
      event.preventDefault();
      event.stopPropagation();
      this.handleProductSubmit();
    });
  }

  handleProductSubmit() {
    const variant = this.currentVariant();

    if (!variant || !variant.id || this.isVariantSoldOut(variant)) {
      return;
    }

    const akoaModal = this.root.querySelector('[data-akao-modal]');
    const akoaRequired = this.root.dataset.akoaRequired === 'true' || (akoaModal && akoaModal.dataset.required === 'true');

    if (akoaRequired && akoaModal && !this.akoaAccepted) {
      this.pendingAddToCartAfterAkoa = true;
      akoaModal.hidden = false;
      return;
    }

    if (this.isPersonalized()) {
      if (!this.personalizationInput || !this.personalizationInput.value.trim()) {
        if (this.personalizationInput) this.personalizationInput.focus();
        return;
      }

      if (!this.engravingVariantId) {
        return;
      }

      this.addPersonalizedCartItems();
      return;
    }

    this.addStandardCartItem();
  }

  setAddToCartLoading(isLoading) {
    if (!this.addToCart) return;

    this.addToCart.disabled = isLoading;
    this.addToCart.classList.toggle('is-loading', isLoading);
  }

  redirectToCart() {
    window.location.href = '/cart';
  }

  addStandardCartItem() {
    if (!this.form) return;

    this.setAddToCartLoading(true);

    const formData = new FormData(this.form);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    })
      .then(response => {
        if (!response.ok) throw new Error('Product add to cart failed');
        return response.json();
      })
      .then(() => this.redirectToCart())
      .catch(() => {
        this.setAddToCartLoading(false);
        this.form.submit();
      });
  }

  addPersonalizedCartItems() {
    if (!this.form || !this.personalizationInput) return;

    this.setAddToCartLoading(true);

    const key = `eng-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const engravingText = this.personalizationInput.value.trim();
    const formData = new FormData(this.form);

    formData.set('properties[Personalization]', 'Personalized');
    formData.set('properties[Custom Blade Engraving]', engravingText);
    formData.set('properties[_engraving_key]', key);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    })
      .then(response => {
        if (!response.ok) throw new Error('Personalized product add to cart failed');
        return response.json();
      })
      .then(() => fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: this.engravingVariantId,
          quantity: this.getQty(),
          properties: {
            'Custom Blade Engraving': engravingText,
            'For Product': this.product.title,
            '_engraving_addon': 'true',
            '_engraving_key': key
          }
        })
      }))
      .then(response => {
        if (!response.ok) throw new Error('Engraving add-on add to cart failed');
        return response.json();
      })
      .then(() => this.redirectToCart())
      .catch(() => {
        this.setAddToCartLoading(false);
        this.redirectToCart();
      });
  }

  isVariantSoldOut(variant) {
    if (!variant) return false;

    const variantId = Number(variant.id);

    if (this.soldOutVariantIds && this.soldOutVariantIds.has(variantId)) {
      return true;
    }

    const inventoryQuantity = Number(variant.inventory_quantity || 0);

    return variant.available === false || inventoryQuantity <= 0;
  }

  updateFromVariant(variant) {
    if (!variant) return;

    const isSoldOut = this.isVariantSoldOut(variant);
    if (this.variantInput) this.variantInput.value = variant.id;

    const personalizedPrice = variant.price + (this.isPersonalized() ? this.engravingPrice : 0);
    const money = this.formatMoney(personalizedPrice);

    this.priceEls.forEach(el => { el.textContent = money; });

    if (this.addToCartText) {
      const buttonLabel = variant.available ? `Add to Cart | ${money}` : 'Out Of Stock';
      this.addToCartText.innerHTML = buttonLabel.replace(' | ', ' &nbsp;|&nbsp; ');
      if (this.addToCart) this.addToCart.setAttribute('data-button-label', buttonLabel);
    }

    if (this.addToCart) {
      this.addToCart.disabled = isSoldOut;
      this.addToCart.classList.toggle('sold', isSoldOut);
    }
    if (this.actions) {
      this.actions.classList.toggle('soldOut', isSoldOut);
      this.actions.hidden = isSoldOut;
      this.actions.style.display = isSoldOut ? 'none' : '';
    }

    this.setWaitlistVisible(isSoldOut);

    if (this.personalization) {
      const personalizationWrap = this.personalization.closest('.tk-product-block--personalization') || this.personalization;
      personalizationWrap.hidden = isSoldOut;
      personalizationWrap.style.display = isSoldOut ? 'none' : '';

      if (isSoldOut) {
        this.clearPersonalizationForSoldOut();
      }
    }

    if (this.sezzleWidget) {
      this.sezzleWidget.setAttribute('data-price', (Number(personalizedPrice || 0) / 100).toFixed(2));
      this.sezzleWidget.setAttribute('data-amount', (Number(personalizedPrice || 0) / 100).toFixed(2));
    }

    if (this.badge) {
      const threshold = Number(this.root.dataset.lowStockThreshold || 15);
      const inventory = Number(variant.inventory_quantity || 0);
      if (isSoldOut || inventory === 0) {
        this.badge.hidden = false;
        this.badge.textContent = 'Out Of Stock';
        this.badge.classList.add('tk-product-badge--sold', 'nohde');
        this.badge.classList.remove('tk-product-badge--low', 'Lowstck', 'tk-product-badge--new');
      } else if (this.isNewSeriesProduct) {
        this.badge.hidden = false;
        this.badge.textContent = 'New Series';
        this.badge.classList.add('tk-product-badge--new');
        this.badge.classList.remove('tk-product-badge--sold', 'nohde', 'tk-product-badge--low', 'Lowstck');
      } else if (inventory > 0 && inventory < threshold) {
        this.badge.hidden = false;
        this.badge.textContent = 'Low on stock';
        this.badge.classList.add('tk-product-badge--low', 'Lowstck');
        this.badge.classList.remove('tk-product-badge--sold', 'nohde', 'tk-product-badge--new');
      } else {
        this.badge.hidden = true;
      }
    }
  }

  clearPersonalizationForSoldOut() {
    if (!this.personalization) return;

    this.personalization.classList.remove('is-active');

    if (this.personalizationInput) {
      this.personalizationInput.value = '';
      this.personalizationInput.disabled = true;
      this.personalizationInput.required = false;
    }

    if (this.personalizationType) {
      this.personalizationType.value = 'Standard';
    }
  }

  resetWaitlistState() {
    this.waitlist = this.root.querySelector('[data-waitlist]');
    this.waitlistForm = this.root.querySelector('[data-waitlist-form]') || this.root.querySelector('#notify-me-container');
    this.waitlistSuccess = this.root.querySelector('[data-waitlist-success]');
    if (this.waitlist) {
      this.waitlist.hidden = false;
      this.waitlist.style.display = 'block';
    }
    const open = this.root.querySelector('[data-waitlist-open]');
    const submit = this.root.querySelector('[data-waitlist-submit]');
    const error = this.root.querySelector('[data-waitlist-error]');
    this.root.querySelectorAll('[data-waitlist-open]').forEach(button => {
      button.hidden = false;
      button.removeAttribute('aria-hidden');
      button.style.setProperty('display', 'flex', 'important');
      button.style.removeProperty('visibility');
      button.style.removeProperty('opacity');
    });
    if (this.waitlistForm) {
      this.waitlistForm.hidden = true;
      this.waitlistForm.style.display = 'none';
    }
    if (this.waitlistSuccess) {
      this.waitlistSuccess.hidden = true;
      this.waitlistSuccess.style.display = 'none';
    }
    if (submit) {
      submit.disabled = false;
      submit.classList.remove('is-loading');
    }
    if (error) error.hidden = true;
  }

  updateDeliveryDate() {
    const el = this.root.querySelector('[data-delivery-date]');
    if (!el) return;
    const date = new Date();
    date.setDate(date.getDate() + 4);
    el.textContent = date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-main]').forEach(root => new TKProductMain(root));
});

/*
  Capture-phase Add to Cart safety handler.
  This runs before older theme/app click handlers and guarantees the TK Product Main
  add-to-cart button posts the selected variant to Shopify.
*/
(function installTKProductMainAddToCartSafetyHandler() {
  if (window.__TK_PRODUCT_MAIN_ATC_SAFETY__) return;
  window.__TK_PRODUCT_MAIN_ATC_SAFETY__ = true;

  function getQuantity(root) {
    var input = root.querySelector('[data-quantity], input[name="quantity"]');
    return Math.max(1, Number(input && input.value) || 1);
  }

  function isPersonalized(root) {
    var typeInput = root.querySelector('[data-personalization-type], input[name="properties[Personalization]"]');
    var engravingInput = root.querySelector('[data-personalization-input], input[name="properties[Custom Blade Engraving]"]');

    return Boolean(
      typeInput &&
      typeInput.value === 'Personalized' &&
      engravingInput &&
      !engravingInput.disabled
    );
  }

  function getEngravingText(root) {
    var engravingInput = root.querySelector('[data-personalization-input], input[name="properties[Custom Blade Engraving]"]');
    return engravingInput ? engravingInput.value.trim() : '';
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    button.disabled = Boolean(loading);
    button.classList.toggle('is-loading', Boolean(loading));
  }

  function submitStandard(form, button) {
    var formData = new FormData(form);

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    }).then(function (response) {
      if (!response.ok) throw new Error('Standard add to cart failed');
      return response.json();
    }).then(function () {
      window.location.href = '/cart';
    }).catch(function () {
      setButtonLoading(button, false);
      HTMLFormElement.prototype.submit.call(form);
    });
  }

  function submitWithEngraving(root, form, button) {
    var engravingVariantId = Number(root.dataset.engravingVariantId || 0);
    var engravingText = getEngravingText(root);
    var engravingInput = root.querySelector('[data-personalization-input], input[name="properties[Custom Blade Engraving]"]');

    if (!engravingText) {
      if (engravingInput) engravingInput.focus();
      setButtonLoading(button, false);
      return Promise.resolve();
    }

    if (!engravingVariantId) {
      setButtonLoading(button, false);
      return Promise.resolve();
    }

    var key = 'eng-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8);
    var formData = new FormData(form);

    formData.set('properties[Personalization]', 'Personalized');
    formData.set('properties[Custom Blade Engraving]', engravingText);
    formData.set('properties[_engraving_key]', key);

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: formData
    }).then(function (response) {
      if (!response.ok) throw new Error('Personalized product add to cart failed');
      return response.json();
    }).then(function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: engravingVariantId,
          quantity: getQuantity(root),
          properties: {
            'Custom Blade Engraving': engravingText,
            'For Product': root.dataset.productTitle || (window.product && window.product.title) || '',
            '_engraving_addon': 'true',
            '_engraving_key': key
          }
        })
      });
    }).then(function (response) {
      if (!response.ok) throw new Error('Engraving add-on add to cart failed');
      return response.json();
    }).then(function () {
      window.location.href = '/cart';
    }).catch(function () {
      setButtonLoading(button, false);
      window.location.href = '/cart';
    });
  }

  function submitTKProduct(button) {
    var root = button.closest('[data-product-main]');
    if (!root) return;

    var form = root.querySelector('[data-product-form], form[action="/cart/add"]');
    if (!form) return;

    if (button.disabled || button.classList.contains('sold')) return;

    var modal = root.querySelector('[data-akao-modal]');
    var akoaRequired = root.dataset.akoaRequired === 'true' || (modal && modal.dataset.required === 'true');

    if (akoaRequired && modal && root.dataset.akoaAccepted !== 'true') {
      root.dataset.pendingAddToCart = 'true';
      modal.hidden = false;
      return;
    }

    setButtonLoading(button, true);

    if (isPersonalized(root)) {
      submitWithEngraving(root, form, button);
    } else {
      submitStandard(form, button);
    }
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.tk-product-main [data-add-to-cart], .tk-product-main .btn-template');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    submitTKProduct(button);
  }, true);

  document.addEventListener('click', function (event) {
    var accept = event.target.closest('.tk-product-main [data-akao-accept]');
    if (!accept) return;

    var root = accept.closest('[data-product-main]');
    if (!root) return;

    root.dataset.akoaAccepted = 'true';

    if (root.dataset.pendingAddToCart === 'true') {
      root.dataset.pendingAddToCart = 'false';
      var button = root.querySelector('[data-add-to-cart]');
      if (button) submitTKProduct(button);
    }
  }, true);
})();


/*
  TK Product sticky bar.
  Shows after scrolling past the product main section and delegates add-to-cart
  back to the main product form so existing cart/engraving logic stays intact.
*/
(function installTKProductStickyBars() {
  if (window.__TK_PRODUCT_STICKY_BARS__) return;
  window.__TK_PRODUCT_STICKY_BARS__ = true;

  function money(cents) {
    return '$' + (Number(cents || 0) / 100).toFixed(2);
  }

  function safeJSON(value) {
    try { return JSON.parse(value); } catch (e) { return null; }
  }

  function getHeaderOffset() {
    var candidates = [
      'sticky-header',
      '.shopify-section-header-sticky',
      '#shopify-section-header',
      '.section-header',
      'header[role="banner"]',
      'header'
    ];

    for (var i = 0; i < candidates.length; i += 1) {
      var header = document.querySelector(candidates[i]);
      if (!header) continue;

      var style = window.getComputedStyle(header);
      if (style.position === 'fixed' || style.position === 'sticky') {
        return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
      }
    }

    return 0;
  }

  function selectedOptions(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-option-position]')).map(function (select) {
      return select.value;
    });
  }

  function currentVariant(root, product) {
    if (!product || !product.variants) return null;

    var selected = selectedOptions(root);
    if (selected.length) {
      var match = product.variants.find(function (variant) {
        return variant.options.every(function (option, index) {
          return String(option) === String(selected[index]);
        });
      });

      if (match) return match;
    }

    var variantInput = root.querySelector('[data-variant-id], input[name="id"]');
    var id = variantInput ? Number(variantInput.value) : Number(root.dataset.currentVariantId);

    return product.variants.find(function (variant) {
      return Number(variant.id) === id;
    }) || product.variants[0] || null;
  }

  function isSoldOut(root, variant) {
    if (!variant) return true;
    if (root.classList.contains('tk-product-main--gift-card')) return false;

    var soldOutIds = new Set(String(root.dataset.soldOutVariantIds || '').split(',').filter(Boolean).map(function (id) {
      return Number(id);
    }));

    if (soldOutIds.has(Number(variant.id))) return true;

    return variant.available === false || Number(variant.inventory_quantity || 0) <= 0;
  }

  function setStickyQty(root, value) {
    var stickyQty = root.querySelector('[data-sticky-quantity]');
    var mainQty = root.querySelector('[data-quantity], input[name="quantity"]');
    var qty = Math.max(1, Number(value) || 1);

    if (stickyQty) stickyQty.value = qty;
    if (mainQty) mainQty.value = qty;
  }

  function syncStickyFromMain(root, product) {
    var variant = currentVariant(root, product);
    var stickyButton = root.querySelector('[data-sticky-add-to-cart]');
    var stickyActions = root.querySelector('[data-sticky-actions]');
    var stickySelects = root.querySelectorAll('[data-sticky-option-position]');
    var mainSelects = root.querySelectorAll('[data-option-position]');

    stickySelects.forEach(function (stickySelect) {
      var position = stickySelect.dataset.stickyOptionPosition;
      var mainSelect = Array.prototype.find.call(mainSelects, function (select) {
        return String(select.dataset.optionPosition) === String(position);
      });

      if (mainSelect && stickySelect.value !== mainSelect.value) {
        stickySelect.value = mainSelect.value;
      }
    });

    if (!stickyButton || !variant) return;

    var soldOut = isSoldOut(root, variant);
    var label = soldOut ? 'Out Of Stock' : 'Add to Cart | ' + money(variant.price);

    stickyButton.textContent = label;
    stickyButton.dataset.stickyButtonLabel = label;
    stickyButton.disabled = soldOut;

    if (stickyActions) {
      stickyActions.classList.toggle('is-sold-out', soldOut);
    }
  }

  function initSticky(root) {
    var sticky = root.querySelector('[data-product-sticky-bar]');
    if (!sticky) return;

    var product = safeJSON(root.dataset.productJson) || window.product || {};
    var grid = root.querySelector('.tk-product-main__grid') || root;
    var mainButton = root.querySelector('[data-add-to-cart]');

    function updateVisibility() {
      var offset = getHeaderOffset();
      var rect = grid.getBoundingClientRect();
      var shouldShow = rect.bottom <= offset;

      root.style.setProperty('--tk-product-sticky-offset', offset + 'px');
      sticky.hidden = false;
      sticky.classList.toggle('is-visible', shouldShow);
    }

    root.querySelectorAll('[data-sticky-option-position]').forEach(function (stickySelect) {
      stickySelect.addEventListener('change', function () {
        var position = stickySelect.dataset.stickyOptionPosition;
        var mainSelect = Array.prototype.find.call(root.querySelectorAll('[data-option-position]'), function (select) {
          return String(select.dataset.optionPosition) === String(position);
        });

        if (!mainSelect) return;

        mainSelect.value = stickySelect.value;
        mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
        setTimeout(function () { syncStickyFromMain(root, product); }, 30);
      });
    });

    root.querySelectorAll('[data-option-position]').forEach(function (mainSelect) {
      mainSelect.addEventListener('change', function () {
        setTimeout(function () { syncStickyFromMain(root, product); }, 30);
      });
    });

    var minus = root.querySelector('[data-sticky-qty-minus]');
    var plus = root.querySelector('[data-sticky-qty-plus]');
    var qty = root.querySelector('[data-sticky-quantity]');

    if (minus) {
      minus.addEventListener('click', function () {
        setStickyQty(root, Number(qty && qty.value || 1) - 1);
      });
    }

    if (plus) {
      plus.addEventListener('click', function () {
        setStickyQty(root, Number(qty && qty.value || 1) + 1);
      });
    }

    var stickyButton = root.querySelector('[data-sticky-add-to-cart]');
    if (stickyButton) {
      stickyButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        syncStickyFromMain(root, product);
        setStickyQty(root, Number(qty && qty.value || 1));

        if (mainButton && !stickyButton.disabled) {
          mainButton.click();
        }
      });
    }

    syncStickyFromMain(root, product);
    updateVisibility();

    window.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', function () {
      syncStickyFromMain(root, product);
      updateVisibility();
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-product-main]').forEach(initSticky);
  });
})();
